import { t } from '@trimble/i18n';
import {
  isItemLoading,
  isItemLoadingOrLoaded,
  LoadState,
} from '@trimble/data-loading';
import { AlignmentSettings } from '@trimble/types';
import { SectionMaterialConfig } from '@trimble/web3d-providers';
import { produce, type SetStoreFunction } from 'solid-js/store';
import { MissionPathItem, ProjectDataState } from './DataStateModels';
import { generateSectionMaterials } from '../content-loaders/trajectory';
import { useLogger } from '@trimble/solidjs-utils';
import { ILogger } from '@trimble/logging';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { getVisibleGaussianSplatIds } from './gaussian-splat-visibility';

function logger(): ILogger {
  return useLogger().getLogger('DataContextProviderHelpers');
}

export const isPointCloudLoading = (
  dataState: ProjectDataState,
  pointCloudId: string
): boolean => {
  if (dataState.pointCloud.state != LoadState.LoadComplete) {
    return false;
  }

  return isItemLoading(dataState.pointCloud.items[pointCloudId]?.loadState);
};

export const isPointCloudVisible = (
  dataState: ProjectDataState,
  pointCloudId: string
): boolean => {
  if (dataState.pointCloud.state != LoadState.LoadComplete) {
    return false;
  }

  const pointCloud = dataState.pointCloud.items[pointCloudId];

  // eslint-disable-next-line sonarjs/different-types-comparison -- index access can be undefined at runtime (noUncheckedIndexedAccess is off)
  if (pointCloud === undefined) {
    logger().debug('Point cloud not found in project', {
      pointCloudId,
    });
    return false;
  }

  return isItemLoadingOrLoaded(pointCloud.loadState);
};

export const getVisiblePointCloudIds = (
  dataState: ProjectDataState
): string[] | undefined => {
  if (dataState.pointCloud.state != LoadState.LoadComplete) {
    return undefined;
  }

  return Object.keys(dataState.pointCloud.items).filter((pointCloudId) => {
    return isPointCloudVisible(dataState, pointCloudId);
  });
};

export const isTrajectoryLoading = (
  dataState: ProjectDataState,
  trajectoryId: string
): boolean => {
  if (dataState.trajectory.state != LoadState.LoadComplete) {
    return false;
  }

  return isItemLoading(dataState.trajectory.items[trajectoryId]?.loadState);
};

export const isTrajectoryVisible = (
  dataState: ProjectDataState,
  trajectoryId: string
): boolean => {
  if (dataState.trajectory.state != LoadState.LoadComplete) {
    return false;
  }
  const trajectory = dataState.trajectory.items[trajectoryId];

  // eslint-disable-next-line sonarjs/different-types-comparison -- index access can be undefined at runtime (noUncheckedIndexedAccess is off)
  if (trajectory === undefined) {
    logger().debug('Trajectory not found in project', {
      trajectoryId,
    });
    return false;
  }

  return isItemLoadingOrLoaded(trajectory.loadState);
};

/**
 * True when at least one alignment trajectory is loaded and visible (mission path
 * or standalone `trn:alignment:` trajectory without a mission parent).
 */
export const hasLoadedAlignmentInProject = (
  dataState: ProjectDataState
): boolean => {
  const missionState = dataState.mission;
  if (missionState.state === LoadState.LoadComplete) {
    for (const mission of Object.values(missionState.items)) {
      const pathId = mission.pathId;
      if (
        pathId != null &&
        pathId !== '' &&
        isTrajectoryVisible(dataState, pathId)
      ) {
        return true;
      }
    }
  }
  const trajectoryState = dataState.trajectory;
  if (trajectoryState.state !== LoadState.LoadComplete) {
    return false;
  }
  for (const item of Object.values(trajectoryState.items)) {
    if (item.mission !== undefined) {
      continue;
    }
    if (
      item.id.startsWith('trn:alignment:') &&
      isTrajectoryVisible(dataState, item.id)
    ) {
      return true;
    }
  }
  return false;
};

export const getVisibleTrajectoryIds = (
  dataState: ProjectDataState
): string[] | undefined => {
  if (dataState.trajectory.state != LoadState.LoadComplete) {
    return undefined;
  }

  return Object.keys(dataState.trajectory.items).filter((trajectoryId) => {
    return isTrajectoryVisible(dataState, trajectoryId);
  });
};

export const isModelLoading = (
  dataState: ProjectDataState,
  modelId: string
): boolean => {
  if (dataState.tcFileFolder.state != LoadState.LoadComplete) {
    return false;
  }

  return isItemLoading(dataState.tcFileFolder.items[modelId]?.loadState);
};

export const isModelVisible = (
  dataState: ProjectDataState,
  modelId: string
): boolean => {
  if (dataState.tcFileFolder.state != LoadState.LoadComplete) {
    return false;
  }

  const model = dataState.tcFileFolder.items[modelId];

  // eslint-disable-next-line sonarjs/different-types-comparison -- index access can be undefined at runtime (noUncheckedIndexedAccess is off)
  if (model === undefined) {
    logger().error('Model not found', { modelId });
    return false;
  }

  return isItemLoadingOrLoaded(model.loadState);
};

export const getVisibleModelIds = (
  dataState: ProjectDataState
): string[] | undefined => {
  if (dataState.tcFileFolder.state != LoadState.LoadComplete) {
    return undefined;
  }

  return Object.keys(dataState.tcFileFolder.items).filter((modelId) => {
    return isModelVisible(dataState, modelId);
  });
};

/** True when at least one point cloud, trajectory, model, or Gaussian splat is loaded/loading in the viewer. */
export const isProjectGeometryVisible = (
  dataState: ProjectDataState
): boolean => {
  const pointClouds = getVisiblePointCloudIds(dataState) ?? [];
  const trajectories = getVisibleTrajectoryIds(dataState) ?? [];
  const models = getVisibleModelIds(dataState) ?? [];
  const gaussianSplats = getVisibleGaussianSplatIds(dataState) ?? [];
  return (
    pointClouds.length > 0 ||
    trajectories.length > 0 ||
    models.length > 0 ||
    gaussianSplats.length > 0
  );
};

/**
 * Determines whether a full API reload is needed (as opposed to a local
 * retransform from the cached buffer) when the alignment snapshot changes.
 *
 * A full reload is required when:
 *  - `prev` is undefined (first execution from SolidJS's `on()`), or
 *  - the labelInterval changed — the API must regenerate markups at the
 *    new spacing.
 */
export function needsFullAlignmentReload(
  settings: AlignmentSettings,
  prev: AlignmentSettings | undefined
): boolean {
  return prev === undefined || settings.labelInterval !== prev.labelInterval;
}

/**
 * Gets the section names that should be loaded for a given mission path based on which point clouds are currently loaded.
 * @param dataState The current project data state
 * @param missionPathId The ID of the mission path (trajectory)
 * @returns Array of section names that should be marked as loaded
 */
export const getLoadedSectionsForMissionPath = (
  dataState: ProjectDataState,
  missionPathId: string
): string[] => {
  if (
    dataState.trajectory.state !== LoadState.LoadComplete ||
    dataState.mission.state !== LoadState.LoadComplete ||
    dataState.pointCloud.state !== LoadState.LoadComplete
  ) {
    return [];
  }

  const trajectory = dataState.trajectory.items[missionPathId];
  if (!trajectory?.mission) {
    return [];
  }
  const mission = trajectory.mission;
  const loadedPointClouds = dataState.pointCloud.items;
  // Treat Loading as still-loaded: registering another pane briefly flips
  // item loadState to Loading and must not clear section highlighting.
  return mission.runs
    .filter((run) => {
      const state = loadedPointClouds[run.id]?.loadState;
      // eslint-disable-next-line sonarjs/different-types-comparison -- loadState can be missing for runs that are not loaded yet
      return state !== undefined && isItemLoadingOrLoaded(state);
    })
    .map((run) => run.name);
};

export const getLoadedSectionsByPath = (
  dataState: ProjectDataState
): Map<string, string[]> => {
  const byPath = new Map<string, string[]>();
  if (dataState.trajectory.state !== LoadState.LoadComplete) {
    return byPath;
  }

  for (const trajectory of Object.values(dataState.trajectory.items)) {
    if (
      trajectory.mission &&
      trajectory.isSplittable === true &&
      isItemLoadingOrLoaded(trajectory.loadState)
    ) {
      byPath.set(
        trajectory.id,
        getLoadedSectionsForMissionPath(dataState, trajectory.id)
      );
    }
  }
  return byPath;
};

export const getSectionMaterialsByPath = (
  dataState: ProjectDataState
): Map<string, SectionMaterialConfig[]> => {
  const byPath = new Map<string, SectionMaterialConfig[]>();
  if (dataState.trajectory.state !== LoadState.LoadComplete) {
    return byPath;
  }

  for (const trajectory of Object.values(dataState.trajectory.items)) {
    if (
      trajectory.mission &&
      trajectory.isSplittable === true &&
      isItemLoadingOrLoaded(trajectory.loadState)
    ) {
      byPath.set(
        trajectory.id,
        generateSectionMaterials(trajectory as MissionPathItem)
      );
    }
  }
  return byPath;
};

export function setMissionRunNumberOfStations(
  setDataState: SetStoreFunction<ProjectDataState>,
  missionTrn: string,
  runId: string,
  numberOfStations: number | undefined
): void {
  setDataState(
    'mission',
    produce((mission) => {
      if (mission.state !== LoadState.LoadComplete || !('items' in mission)) {
        return;
      }
      const missionItem = Object.values(mission.items).find(
        (m) => m.missionTrn === missionTrn
      );
      if (missionItem === undefined) {
        return;
      }
      const run = missionItem.runs.find((r) => r.runId === runId);
      if (run === undefined) {
        return;
      }
      run.numberOfStations = numberOfStations;
    })
  );
}

export function handleQueryError(
  key: keyof ProjectDataState,
  setDataState: SetStoreFunction<ProjectDataState>,
  error: unknown
): void {
  NotificationEventBus.emit({
    type: 'error',
    message: t('general.unhandledError'),
  });

  setDataState(key, { state: LoadState.Error });

  logger().error(`Error loading ${key}`, {
    error,
  });
}
